import React from "react";
import { AiIcon, SearchChip, Tooltip, VerticalDivider } from "@sscs/ui";
import { Insight } from "../../../types";
import { getTotalReleaseStatusCount } from "../../../utils";
import ReleaseStatusCount from "./ReleaseStatusCount";
import styles from "./summary.module.scss";
import { useSearchActions } from "./useSearchActions";
import VersionCountDetails from "./VersionCountDetails";

const VulnerabilityDescription = ({
  description,
}: {
  description: string;
}): React.JSX.Element => {
  return (
    <>
      <Tooltip trigger="graphic" title="AI generated summary">
        <AiIcon className={styles["ai-icon"]} />
      </Tooltip>
      {description ?? "AI summary not yet generated."}
    </>
  );
};

type SummaryProps = {
  issue: Insight;
};

const Summary = ({ issue }: SummaryProps): React.JSX.Element => {
  const searchActions = useSearchActions(issue);

  return (
    <div className={styles["issue-summary"]}>
      <div className={styles["description-and-actions"]}>
        <div className={styles.description}>
          {issue.issue_type === "vulnerability" ? (
            <VulnerabilityDescription description={issue.short_description} />
          ) : (
            issue.short_description
          )}
        </div>
        <div className={styles["action-chips"]}>
          {searchActions
            .filter((action) => !action.presentInFilter)
            .map((action) => (
              <Tooltip key={action.label} title={action.tooltip}>
                <SearchChip
                  label={
                    action.isNegativeFilter ? (
                      <>
                        <span className={styles["negative-filter"]}>NOT</span>
                        &nbsp;
                        {action.label}
                      </>
                    ) : (
                      action.label
                    )
                  }
                  onClick={action.onClick}
                />
              </Tooltip>
            ))}
        </div>
      </div>
      <VerticalDivider />
      <div className={styles["pass-fail-statistics"]}>
        <ReleaseStatusCount
          label={`Affected packages (${getTotalReleaseStatusCount(
            issue.counters.affected_packages,
          ).toLocaleString()})`}
          count={issue.counters.affected_packages}
          className={styles["packages-bar"]}
        />
        <ReleaseStatusCount
          label={`Affected versions (${getTotalReleaseStatusCount(
            issue.counters.affected_versions,
          ).toLocaleString()})`}
          count={issue.counters.affected_versions}
        />
        <div className={styles["secondary-data"]}>
          <VersionCountDetails
            title="Released"
            count={issue.counters.released_versions}
          />
          <VersionCountDetails
            title="Approved"
            count={issue.counters.approved_versions}
          />
        </div>
      </div>
    </div>
  );
};

export default Summary;
